import type { Edge, Node } from '@xyflow/react';
import type * as Api from '@ViewModels';
import type { BaseNodeData } from '../types';
import { NODE_TYPES } from '../types';
import { extractPrimaryAndAdditionalCriteria } from './segmentCriteria';
import type { SegmentNodeData } from '../types/stepNodes';
import { EDGE_TYPES } from '../types/edges';

interface PersistedSwitchCaseAssignment {
	caseId?: string;
	caseName?: string;
	childTemplateId?: string;
	condition?: Api.IContactFilterCriteria | null;
	stageIds?: string[];
	prompt?: string;
	isDefault: boolean;
}

function isSegmentNodeForSwitch(
	node: Node<BaseNodeData>,
	optimisticSwitchNodeId: string
): node is Node<SegmentNodeData> {
	if (node.type !== NODE_TYPES.SEGMENT) {
		return false;
	}

	const data = node.data as SegmentNodeData;
	return data.parentSwitchNodeId === optimisticSwitchNodeId;
}

function buildCaseBuckets(cases: Api.IAutomationStepCaseStatement[]) {
	const nonDefaultCases = cases.filter(candidate => candidate.isDefault !== true);
	const defaultCases = cases.filter(candidate => candidate.isDefault === true);

	return {
		nonDefaultCases,
		defaultCases,
	};
}

export function buildPersistedSwitchCaseAssignments({
	nodes,
	optimisticSwitchNodeId,
	persistedSwitchStep,
}: {
	nodes: Node<BaseNodeData>[];
	optimisticSwitchNodeId: string;
	persistedSwitchStep: Api.ISwitchAutomationStep;
}): Map<string, PersistedSwitchCaseAssignment> {
	const localSegments = nodes
		.filter(node => isSegmentNodeForSwitch(node, optimisticSwitchNodeId))
		.sort((a, b) => {
			const aIndex = (a.data as SegmentNodeData).segmentIndex;
			const bIndex = (b.data as SegmentNodeData).segmentIndex;
			return aIndex - bIndex;
		});

	const cases = persistedSwitchStep.cases || [];
	const { nonDefaultCases, defaultCases } = buildCaseBuckets(cases);
	const assignments = new Map<string, PersistedSwitchCaseAssignment>();

	for (const segment of localSegments) {
		const segmentData = segment.data as SegmentNodeData;
		const matchingCase = segmentData.isDefault ? defaultCases.shift() : nonDefaultCases.shift();
		if (!matchingCase) {
			continue;
		}

		assignments.set(segment.id, {
			caseId: matchingCase.id,
			caseName: matchingCase.name,
			childTemplateId: matchingCase.automationTemplateId,
			condition: matchingCase.filter,
			stageIds: matchingCase.stageIds ?? [],
			prompt: matchingCase.prompt || undefined,
			isDefault: matchingCase.isDefault === true,
		});
	}

	return assignments;
}

export function buildSwitchOutputHandles(cases: Api.IAutomationStepCaseStatement[] | undefined): string[] {
	if (!cases || cases.length === 0) {
		return ['default'];
	}

	return cases.map((candidate, index) => (candidate.isDefault ? 'default' : `case-${index}`));
}

export function syncOptimisticSwitchNodes({
	nodes,
	optimisticSwitchNodeId,
	persistedSwitchStep,
	automationTemplate,
	caseAssignments,
}: {
	nodes: Node<BaseNodeData>[];
	optimisticSwitchNodeId: string;
	persistedSwitchStep: Api.ISwitchAutomationStep;
	automationTemplate?: Api.IAutomationTemplate;
	caseAssignments: Map<string, PersistedSwitchCaseAssignment>;
}): Node<BaseNodeData>[] {
	return nodes.map(node => {
		if (node.id === optimisticSwitchNodeId) {
			return {
				...node,
				data: {
					...node.data,
					stepId: persistedSwitchStep.id,
					automationStep: persistedSwitchStep,
					automationTemplate,
					summary: `${persistedSwitchStep.cases?.length ?? 0} branches`,
					outputHandles: buildSwitchOutputHandles(persistedSwitchStep.cases),
					stepData: {
						...(node.data as { stepData?: Record<string, unknown> }).stepData,
						name: persistedSwitchStep.name,
					},
				},
			};
		}

		if (node.type !== NODE_TYPES.SEGMENT) {
			return node;
		}

		const caseAssignment = caseAssignments.get(node.id);
		if (!caseAssignment) {
			return node;
		}

		const segmentData = node.data as SegmentNodeData;
		const { primaryCriteria, additionalCriteria } = extractPrimaryAndAdditionalCriteria(
			caseAssignment.condition || undefined,
			caseAssignment.isDefault
		);

		return {
			...node,
			data: {
				...segmentData,
				caseId: caseAssignment.caseId || segmentData.caseId,
				segmentName: caseAssignment.caseName || segmentData.segmentName,
				childTemplateId: caseAssignment.childTemplateId || segmentData.childTemplateId,
				isDefault: caseAssignment.isDefault,
				primaryCriteria,
				additionalCriteria,
				prompt: caseAssignment.prompt,
				stageIds: caseAssignment.stageIds ?? [],
			},
		};
	});
}

export function syncOptimisticSwitchEdges({
	edges,
	optimisticSwitchNodeId,
	realSwitchNodeId,
	caseAssignments,
}: {
	edges: Edge[];
	optimisticSwitchNodeId: string;
	realSwitchNodeId?: string;
	caseAssignments: Map<string, PersistedSwitchCaseAssignment>;
}): Edge[] {
	return edges.map(edge => {
		if (
			realSwitchNodeId &&
			edge.type === EDGE_TYPES.PHANTOM_ADD_PATH &&
			(edge.data as { switchNodeId?: string } | undefined)?.switchNodeId === optimisticSwitchNodeId
		) {
			return {
				...edge,
				data: {
					...edge.data,
					switchNodeId: realSwitchNodeId,
				},
			};
		}

		if (
			edge.type !== EDGE_TYPES.BRANCH ||
			(edge.data as { switchNodeId?: string } | undefined)?.switchNodeId !== optimisticSwitchNodeId
		) {
			return edge;
		}

		const caseAssignment = caseAssignments.get(edge.target);
		if (!caseAssignment) {
			return edge;
		}

		return {
			...edge,
			data: {
				...edge.data,
				caseId: caseAssignment.caseId,
				caseName: caseAssignment.caseName || (edge.data as { caseName?: string } | undefined)?.caseName,
				condition: caseAssignment.condition,
				isDefault: caseAssignment.isDefault,
			},
		};
	});
}
